import { useMatch, NavLink } from 'react-router-dom';

import { classNames } from 'primereact/utils';
import { Item } from '../../../models/sidebar/sidebar';
import { SidebarMenuItemProps } from '../../../models/sidebar/sidebar';

const SidebarMenuItem = (props: SidebarMenuItemProps) => {
  const { id, minimized, label, to, submenuItems, icon, currentMenu, selectMenu } = props;

  return (
    <div className="flex flex-col overflow-hidden mb-0.5">
      <MenuHeader
        id={id}
        minimized={minimized}
        label={label}
        to={to}
        submenuItems={submenuItems}
        icon={icon}
        currentMenu={currentMenu}
        selectMenu={selectMenu}
      />
      {submenuItems && submenuItems.length > 0 && (
        <div
          className={classNames(
            'transition-all duration-300 overflow-hidden',
            !minimized && currentMenu === id ? 'max-h-[750px] ease-in' : 'max-h-[0px] ease-out'
          )}
        >
          <ul className="list-none overflow-x-hidden overflow-y-auto nav-scrollbar ml-3 border-l border-[var(--sidebar-separator-color)]">
            {submenuItems.map(
              (ele: Item, idx: number): JSX.Element => (
                <li key={idx}>
                  <SubmenuItem
                    key={idx}
                    to={ele.to}
                    label={ele.label}
                    // Exact-match only when a sibling's path is nested under this one (e.g. "Fee
                    // Configurations" -> /fees, siblings "HUD Lines" -> /fees/hud-lines etc.) —
                    // otherwise this item needs prefix matching to stay highlighted on its own
                    // child/detail routes (e.g. "Waterfalls" -> /credit/waterfalls staying active
                    // on /credit/waterfalls/new, which isn't a separate sidebar item).
                    end={submenuItems.some((other) => other !== ele && other.to.startsWith(`${ele.to}/`))}
                  />
                </li>
              )
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

const MenuHeader = (props: SidebarMenuItemProps): JSX.Element => {
  const { id, minimized, label, to, submenuItems, icon, currentMenu, selectMenu } = props;
  const menuHeaderBaseStyle: string =
    'flex flex-row items-center cursor-pointer h-[2.6rem] rounded-lg transition-colors duration-200';
  const urlMatch = useMatch(`${to}/:sub_page`);

  const headerContent = (
    <>
      {icon && (
        <i
          className={classNames(
            'ml-[calc((var(--sidebar-min-width)_-_4rem)/2)] pi text-[1rem]',
            icon
          )}
        ></i>
      )}
      {!minimized && (
        <div className="flex flex-row justify-between items-center ml-3 grow text-[0.85rem]">
          <span>{label}</span>
          {submenuItems && submenuItems.length > 0 && (
            <i
              className={classNames(
                'pi pi-chevron-down text-[0.6rem] opacity-60 duration-300 ease-in-out mr-1',
                !minimized && currentMenu === id && 'rotate-180'
              )}
            ></i>
          )}
        </div>
      )}
    </>
  );

  if (submenuItems && submenuItems.length > 0 && to) {
    return (
      <NavLink
        className={({ isActive }) =>
          classNames(
            menuHeaderBaseStyle,
            'hover:bg-[var(--sidebar-link-bg-color-active)] hover:text-[var(--sidebar-link-text-color-hover)]',
            (isActive || urlMatch) &&
              'bg-[var(--sidebar-link-bg-color-active)] text-[var(--sidebar-link-text-color-active)]'
          )
        }
        to={to}
        onClick={(e) => {
          selectMenu(id);
        }}
      >
        {headerContent}
      </NavLink>
    );
  }

  return submenuItems && submenuItems.length > 0 ? (
    <div
      className={classNames(
        menuHeaderBaseStyle,
        'hover:bg-[var(--sidebar-link-bg-color-active)] hover:text-[var(--sidebar-link-text-color-hover)]',
        to !== '' && urlMatch && 'text-[var(--sidebar-link-text-color-active)]'
      )}
      onClick={() => selectMenu(id)}
    >
      {headerContent}
    </div>
  ) : (
    <NavLink
      className={({ isActive }) =>
        classNames(
          menuHeaderBaseStyle,
          'hover:bg-[var(--sidebar-link-bg-color-active)] hover:text-[var(--sidebar-link-text-color-hover)]',
          isActive &&
            'bg-[var(--sidebar-link-bg-color-active)] text-[var(--sidebar-link-text-color-active)]'
        )
      }
      to={to || ''}
      onClick={() => selectMenu(id)}
    >
      {headerContent}
    </NavLink>
  );
};

const SubmenuItem = (props: { label: string; to: string; end?: boolean }): JSX.Element => {
  const { label, to, end } = props;
  const submenuItemBaseStyle: string =
    'flex flex-row items-center py-[0.45rem] pl-4 text-[0.8rem] transition-colors duration-200 rounded-md';

  return (
    <NavLink
      className={({ isActive }) =>
        classNames(
          submenuItemBaseStyle,
          'hover:text-[var(--sidebar-link-text-color-hover)]',
          isActive
            ? 'text-[var(--sidebar-link-text-color-active)] bg-[var(--sidebar-link-bg-color-active)]'
            : 'text-[var(--sidebar-link-text-color)]'
        )
      }
      to={to}
      end={end}
    >
      <span className="w-1 h-1 rounded-full bg-current opacity-40 mr-3 flex-shrink-0"></span>
      <span>{label}</span>
    </NavLink>
  );
};

export default SidebarMenuItem;
